import { z } from "zod";
export const IdSchema = z.string().min(1);
export const NonEmptyStringSchema = z.string().min(1);
export const IsoTimestampSchema = z.string().datetime({ offset: true });
export const NonNegativeIntegerSchema = z.number().int().nonnegative();
export const PositiveIntegerSchema = z.number().int().positive();
export const ConfidenceSchema = z.number().min(0).max(1);
export const UnknownRecordSchema = z.record(z.string(), z.unknown());
export const SourceKindSchema = z.enum([
    "pdf",
    "spreadsheet",
    "drawing",
    "image",
    "note",
    "email",
    "benchmark",
    "catalog_workbook",
    "rich_decision",
    "manual_entry",
    "other"
]);
export const AuthorityClassSchema = z.enum([
    "rich_live_clarification",
    "baseline_note",
    "rich_rules_workbook",
    "client_spec",
    "client_email",
    "drawing",
    "takeoff",
    "benchmark",
    "proposal_forge_generated",
    "unknown"
]);
export const RegionRefSchema = z
    .object({
    page: PositiveIntegerSchema.optional(),
    x: z.number().nonnegative(),
    y: z.number().nonnegative(),
    width: z.number().positive(),
    height: z.number().positive(),
    unit: z.enum(["px", "pt", "percent"])
})
    .strict();
const nullableOptional = (schema) => z
    .union([schema, z.null()])
    .optional()
    .transform((value) => (value === null ? undefined : value));
export const SourceRefSchema = z
    .object({
    sourceId: IdSchema,
    sourceKind: SourceKindSchema,
    authorityClass: AuthorityClassSchema,
    locator: NonEmptyStringSchema,
    quote: nullableOptional(NonEmptyStringSchema),
    page: nullableOptional(PositiveIntegerSchema),
    sheetName: nullableOptional(NonEmptyStringSchema),
    cellRef: nullableOptional(NonEmptyStringSchema),
    rangeRef: nullableOptional(NonEmptyStringSchema),
    region: nullableOptional(RegionRefSchema),
    hash: nullableOptional(NonEmptyStringSchema)
})
    .strict();
export const StorageRefSchema = z
    .object({
    bucket: NonEmptyStringSchema,
    path: NonEmptyStringSchema,
    hash: NonEmptyStringSchema,
    contentType: NonEmptyStringSchema
})
    .strict();
export const EvidenceKindSchema = z.enum([
    "pdf_text_span",
    "pdf_region",
    "image_region",
    "sheet_range",
    "note_span",
    "email_span",
    "manual_assertion"
]);
export const EvidenceRefSchema = z
    .object({
    evidenceId: IdSchema,
    sourceRef: SourceRefSchema,
    kind: EvidenceKindSchema,
    locator: NonEmptyStringSchema,
    text: NonEmptyStringSchema.optional(),
    region: RegionRefSchema.optional(),
    confidence: ConfidenceSchema.optional()
})
    .strict();
export const DrawingEvidenceKindSchema = z.enum([
    "annotation",
    "designer_comment",
    "schedule_text",
    "legend",
    "dimension_text",
    "other"
]);
/**
 * Provenance for text that is deliberately admitted from a drawing. The
 * document id is mandatory; page/region or a sheet reference must identify
 * where the text came from. `sourceRef` is optional so callers can preserve
 * the existing source ledger without making the agent-facing shape depend on
 * every source-ref field.
 */
export const DrawingEvidenceProvenanceSchema = z
    .object({
    documentId: IdSchema,
    page: PositiveIntegerSchema.optional(),
    sheetRef: NonEmptyStringSchema.optional(),
    region: RegionRefSchema.optional(),
    sourceRef: SourceRefSchema.optional()
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.page === undefined &&
        value.sheetRef === undefined &&
        value.region === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "drawing evidence provenance requires page, sheetRef, or region",
            path: ["page"]
        });
    }
    if (value.region?.page !== undefined &&
        value.page !== undefined &&
        value.region.page !== value.page) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "drawing evidence region.page must match provenance.page",
            path: ["region", "page"]
        });
    }
    const sourceRef = value.sourceRef;
    if (!sourceRef) {
        return;
    }
    if (sourceRef.sourceId !== value.documentId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "drawing evidence sourceRef.sourceId must match provenance.documentId",
            path: ["sourceRef", "sourceId"]
        });
    }
    if (sourceRef.authorityClass !== "drawing") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "drawing evidence sourceRef.authorityClass must be drawing",
            path: ["sourceRef", "authorityClass"]
        });
    }
    const provenancePage = value.page ?? value.region?.page;
    if (provenancePage !== undefined && sourceRef.page !== provenancePage) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "drawing evidence sourceRef.page must match provenance page",
            path: ["sourceRef", "page"]
        });
    }
    if (provenancePage !== undefined &&
        sourceRef.region?.page !== undefined &&
        sourceRef.region.page !== provenancePage) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "drawing evidence sourceRef.region.page must match provenance page",
            path: ["sourceRef", "region", "page"]
        });
    }
});
/**
 * Text from a drawing is agent evidence only when its origin and location are
 * explicit. Keeping this separate from `EvidenceRefSchema` prevents a raw
 * string or an unlabeled note from crossing the engine boundary by accident.
 */
export const LabeledDrawingEvidenceSchema = z
    .object({
    origin: z.literal("drawing"),
    kind: DrawingEvidenceKindSchema,
    text: NonEmptyStringSchema,
    provenance: DrawingEvidenceProvenanceSchema,
    confidence: ConfidenceSchema.optional()
})
    .strict();
/** Short alias for callers that refer to the shape as drawing evidence. */
export const DrawingEvidenceSchema = LabeledDrawingEvidenceSchema;
export const SourceDocumentSchema = z
    .object({
    id: IdSchema,
    orgId: IdSchema,
    projectId: IdSchema.optional(),
    title: NonEmptyStringSchema,
    sourceKind: SourceKindSchema,
    authorityClass: AuthorityClassSchema,
    storageRef: StorageRefSchema.optional(),
    originalFilename: NonEmptyStringSchema.optional(),
    contentType: NonEmptyStringSchema.optional(),
    hash: NonEmptyStringSchema,
    importedAt: IsoTimestampSchema,
    importedBy: NonEmptyStringSchema.optional(),
    metadata: UnknownRecordSchema
})
    .strict();
export const RuleTraceRefSchema = IdSchema;
export const DerivedProvenanceSchema = z
    .object({
    sourceRefs: z.array(SourceRefSchema),
    ruleTraceRefs: z.array(RuleTraceRefSchema)
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.sourceRefs.length + value.ruleTraceRefs.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "derived objects require at least one sourceRef or ruleTraceRef",
            path: ["sourceRefs"]
        });
    }
});
export const requireSourceOrRuleRef = (schema) => schema.superRefine((value, ctx) => {
    const candidate = value;
    const sourceCount = candidate.sourceRefs?.length ?? 0;
    const ruleTraceCount = candidate.ruleTraceRefs?.length ?? 0;
    if (sourceCount + ruleTraceCount === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "derived objects require at least one sourceRef or ruleTraceRef",
            path: ["sourceRefs"]
        });
    }
});
//# sourceMappingURL=source.js.map